import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import { createSocket } from "../socket/socket";
import { api } from "../api/api";
import { requestForToken, onMessageListener } from "../firebase";
import Loader from "../components/Loader";


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);

  const [userSubData, setUserSubData] = useState({
    subscription: {
      plan: '---',
      remainingBids: '---',
      endDate: '---',
      status: 'idle'
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return "Süresiz";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // 1. fetchUserData: Dışarıdaki bağımlılıklara takılmaması için useCallback içinde tutuyoruz
  const fetchUserData = useCallback(async (userId, currentToken) => {
    if (!userId || !currentToken) return;
    try {
      const response = await api.get(`/subs/UserSubscription/${userId}`);

      const sub = response.data;
      console.log("📡 Abonelik verisi çekildi:", sub);

      setUserSubData({
        subscription: {
          plan: sub.planId || 'free',
          remainingBids: sub.remainingBids ?? 0,
          endDate: sub.endDate ? formatDate(sub.endDate) : 'Süresiz',
          status: sub.status || 'active'
        }
      });
    } catch (err) {
      console.error('❌ fetchUserData Hatası:', err);
    }
  }, []);

  // 2. refreshSocket: DÖNGÜYÜ KIRAN KRİTİK DEĞİŞİKLİK
  // prevSocket kullanarak socket state'ine olan bağımlılığı koparıyoruz
  const refreshSocket = useCallback((newToken) => {
    setSocket((prevSocket) => {
      if (prevSocket) {
        prevSocket.auth.token = newToken;
        prevSocket.disconnect().connect();
        return prevSocket;
      } else {
        return createSocket(newToken);
      }
    });
  }, []); // Bağımlılık dizisi boş kalmalı

  const login = (userData, newToken) => {
    setToken(newToken);
    setUser(userData);
    setSocket(createSocket(newToken));
    // fetchUserData(userData.id, newToken);
  };

  const logout = async () => {
    try {
      await api.post(`/auth/logout`);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      setSocket((prev) => {
        if (prev) prev.disconnect();
        return null;
      });
      setLoading(false);
    }
  };

  // 3. initializeAuth: Sadece sayfa ilk açıldığında bir kez çalışmalı
  useEffect(() => {
    let unsubscribeMessageListener = null;

    const initializeAuth = async () => {
      const storedToken = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get(`/auth/me`);
        const data = res.data;

        if (data.user) {
          setUser(data.user);
          setToken(storedToken);
          setSocket(createSocket(storedToken));
          // await fetchUserData(data.user.id, storedToken);

          // 🔥 Push Bildirim İzni ve Token Al
          requestForToken();

          // 🔥 Foreground Mesaj Dinleyici
          unsubscribeMessageListener = onMessageListener((payload) => {
            console.log("Bildirim alındı (foreground):", payload);
            // Burada istersen bir toast message veya alert gösterebilirsin
            alert(`${payload.notification.title}\n${payload.notification.body}`);
          });
        }
      } catch (err) {
        console.error("Auth init hatası:", err);
        // Eğer bir hata oluştuysa ve interceptor bizi yönlendirmediyse (örn: network error)
        // en azından loading'i kapatmış oluyoruz.
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    return () => {
      if (typeof unsubscribeMessageListener === "function") {
        unsubscribeMessageListener();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Bağımlılık dizisini boş bırakarak sonsuz render döngüsünü engelliyoruz

  const refreshAuth = useCallback(async () => {
    try {
      const res = await api.get(`/auth/me`);
      if (res.data.user) {
        setUser(res.data.user);
      }
    } catch (err) {
      console.error("refreshAuth Hatası:", err);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, userSubData, token, socket, login, logout, setUser, loading, refreshSocket, fetchUserData, refreshAuth }}>
      {!loading ? children : <Loader message="Yükleniyor..." variant="fullscreen" />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
